import { useState, useEffect } from "react";
import { Menu, X, Star } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Programs", id: "programs" },
  { label: "Calendar", id: "calendar" },
  { label: "Parent Info", id: "parent-info" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white/95 shadow-md" : "bg-white/80"
      } backdrop-blur-xl border-b-4 border-yellow-300`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
        <button
          data-testid="logo-home-btn"
          onClick={() => go("home")}
          className="flex items-center gap-2 shrink-0"
        >
          <img src={LOGO_URL} alt="WonderStarzLV Las Vegas logo" className="h-14 w-14 object-contain" />
          <span className="font-display font-bold text-xl text-pink-500 hidden sm:block leading-none">
            WonderStarz<span className="text-emerald-700">LV</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-display font-semibold text-teal-600 hover:text-pink-500 transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-enroll-btn"
            onClick={() => go("enroll")}
            className="bg-pink-500 text-white rounded-full px-6 py-2.5 font-display font-bold hover:bg-pink-600 hover:-translate-y-0.5 hover:shadow-lg transition-all flex items-center gap-1"
          >
            <Star className="w-4 h-4" fill="white" /> Enroll Now
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-pink-500"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t-4 border-yellow-300 px-6 py-4 flex flex-col gap-3">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left font-display font-semibold text-teal-600 hover:text-pink-500 py-1"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-enroll-btn"
            onClick={() => go("enroll")}
            className="bg-pink-500 text-white rounded-full px-6 py-3 font-display font-bold mt-2"
          >
            Enroll Now
          </button>
        </div>
      )}
    </header>
  );
};

export { scrollToId };
